import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'

const empty = { title: '', target_date: '', target_club: '', target_league: '', target_country: '', notes: '', status: 'active' }

export default function Goals({ session }) {
  const uid = session.user.id
  const [goals, setGoals] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(empty)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [filter, setFilter] = useState('active')

  useEffect(() => { load() }, [])

  const load = async () => {
    const { data } = await supabase.from('career_goals').select('*').eq('user_id', uid).order('target_date', { ascending: true })
    setGoals(data || [])
    setLoading(false)
  }

  const save = async () => {
    setSaving(true)
    const payload = { ...form, user_id: uid }
    if (editing) {
      await supabase.from('career_goals').update(payload).eq('id', editing)
    } else {
      await supabase.from('career_goals').insert(payload)
    }
    await load()
    setSaving(false)
    setSaved(true)
    setTimeout(() => {
      setShowModal(false)
      setEditing(null)
      setForm(empty)
      setSaved(false)
    }, 700)
  }

  const del = async (id) => {
    if (!window.confirm('Delete this goal?')) return
    await supabase.from('career_goals').delete().eq('id', id)
    setGoals(prev => prev.filter(g => g.id !== id))
  }

  const updateStatus = async (id, status) => {
    await supabase.from('career_goals').update({ status }).eq('id', id)
    setGoals(prev => prev.map(g => g.id === id ? { ...g, status } : g))
  }

  const openEdit = (g) => {
    setForm(g)
    setEditing(g.id)
    setShowModal(true)
  }

  const filtered = goals.filter(g => filter === 'all' ? true : g.status === filter)

  if (loading) return <div style={{ color: 'var(--muted)' }}>Loading...</div>

  return (
    <div>
      <div className="flex-between mb-32">
        <div>
          <div className="page-title">Career Goals</div>
          <div className="page-sub">Plan your next move</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(empty); setEditing(null); setShowModal(true) }}>+ Add Goal</button>
      </div>

      {/* Stats */}
      <div className="grid-3 mb-32">
        {[
          { value: goals.filter(g => g.status === 'active').length, label: 'Active Goals', color: 'var(--gold)' },
          { value: goals.filter(g => g.status === 'achieved').length, label: 'Achieved', color: '#43A047' },
          { value: goals.length, label: 'Total Goals', color: 'var(--text)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-8 mb-24">
        {['active', 'achieved', 'abandoned', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className="btn btn-ghost" style={{
            fontSize: 12,
            background: filter === f ? 'var(--gold-dim)' : 'transparent',
            borderColor: filter === f ? 'var(--gold-border)' : 'var(--border)',
            color: filter === f ? 'var(--gold)' : 'var(--muted)',
          }}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">◎</div>
            <div className="empty-state-title">No goals yet</div>
            <div className="empty-state-body">Set career goals — target clubs, leagues, timelines. Know where you're heading.</div>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>Add First Goal</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {filtered.map(g => (
            <div key={g.id} className="card" style={{ borderLeft: `3px solid ${g.status === 'achieved' ? '#43A047' : g.status === 'abandoned' ? '#555' : 'var(--gold)'}` }}>
              <div className="flex-between">
                <div style={{ flex: 1 }}>
                  <div className="flex gap-12 mb-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 17, fontWeight: 600 }}>{g.title}</div>
                    <span className={`badge ${g.status === 'achieved' ? 'badge-green' : g.status === 'abandoned' ? 'badge-grey' : 'badge-gold'}`}>{g.status}</span>
                  </div>
                  <div className="flex gap-16" style={{ flexWrap: 'wrap' }}>
                    {g.target_club && <div style={{ fontSize: 13, color: 'var(--muted)' }}>🎯 {g.target_club}</div>}
                    {g.target_league && <div style={{ fontSize: 13, color: 'var(--muted)' }}>🏆 {g.target_league}</div>}
                    {g.target_country && <div style={{ fontSize: 13, color: 'var(--muted)' }}>🌍 {g.target_country}</div>}
                    {g.target_date && <div style={{ fontSize: 13, color: 'var(--muted)' }}>📅 {format(parseISO(g.target_date), 'MMM yyyy')}</div>}
                  </div>
                  {g.notes && <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8, fontStyle: 'italic' }}>{g.notes}</div>}
                </div>
                <div className="flex gap-8" style={{ marginLeft: 16, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {g.status === 'active' && (
                    <button onClick={() => updateStatus(g.id, 'achieved')} className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px', color: '#43A047', borderColor: 'rgba(67,160,71,0.2)' }}>✓ Achieved</button>
                  )}
                  <button onClick={() => openEdit(g)} className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }}>Edit</button>
                  <button onClick={() => del(g.id)} className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">{editing ? 'Edit Goal' : 'Add Goal'}</div>
            <div className="mb-16"><label>Goal Title</label><input value={form.title || ''} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="e.g. Sign for a 2nd division club" /></div>
            <div className="grid-2 mb-16">
              <div><label>Target Club</label><input value={form.target_club || ''} onChange={e => setForm(p => ({...p, target_club: e.target.value}))} placeholder="FC Example" /></div>
              <div><label>Target League</label><input value={form.target_league || ''} onChange={e => setForm(p => ({...p, target_league: e.target.value}))} placeholder="Liga X" /></div>
            </div>
            <div className="grid-2 mb-16">
              <div><label>Target Country</label><input value={form.target_country || ''} onChange={e => setForm(p => ({...p, target_country: e.target.value}))} placeholder="Germany" /></div>
              <div><label>Target Date</label><input type="date" value={form.target_date || ''} onChange={e => setForm(p => ({...p, target_date: e.target.value}))} /></div>
            </div>
            <div className="mb-16">
              <label>Status</label>
              <select value={form.status || 'active'} onChange={e => setForm(p => ({...p, status: e.target.value}))}>
                <option value="active">Active</option>
                <option value="achieved">Achieved</option>
                <option value="abandoned">Abandoned</option>
              </select>
            </div>
            <div className="mb-24"><label>Notes</label><textarea value={form.notes || ''} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} placeholder="Any context or plan..." /></div>
            <div className="flex gap-8">
              <button className="btn btn-primary" onClick={save} disabled={saving || saved}>{saving ? <><span className="btn-spinner" /> Saving...</> : saved ? '✓ Saved' : 'Save Goal'}</button>
              <button className="btn btn-ghost" onClick={() => { setShowModal(false); setEditing(null); setForm(empty) }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
